import React, { useCallback, useEffect, useState } from "react";
import { Wallet, Printer, FileText, Loader2, RefreshCw } from "lucide-react";
import { api, fmtDate, COMPANY } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const Field = ({ label, children }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const Card = ({ label, value, warna, extra }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <div className={`mt-2 font-display text-2xl font-light ${warna}`}>{value}</div>
    {extra && <p className="mt-1 text-[10px] text-slate-500">{extra}</p>}
  </div>
);

const printArea = (id, thermal) => {
  const html = document.getElementById(id)?.innerHTML;
  if (!html) return;
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return toast.error("Popup diblokir browser");
  const st = w.document.createElement("style");
  st.textContent = `@page { size: ${thermal ? "58mm auto" : "A4"}; margin: ${thermal ? "2mm" : "8mm"}; }
    body { font-family: ${thermal ? "monospace" : "Arial, sans-serif"}; color:#000; margin:0;
      padding:${thermal ? "4px" : "0"}; font-size:${thermal ? "11px" : "12px"}; width:${thermal ? "54mm" : "auto"}; }
    table { width:100%; border-collapse:collapse; }
    th,td { border:${thermal ? "none" : "1px solid #2a8c9c"}; padding:3px 5px; font-size:${thermal ? "10px" : "10px"}; }`;
  w.document.head.appendChild(st);
  w.document.body.innerHTML = html;
  w.setTimeout(() => w.print(), 400);
};

export const MaterialBillBoard = () => {
  const [bills, setBills] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [saldo, setSaldo] = useState({ saldo: 0, nomor_berikutnya: "-" });
  const [sel, setSel] = useState([]);
  const [tglFilter, setTglFilter] = useState("");
  const [active, setActive] = useState(null);
  const [jumlah, setJumlah] = useState("");
  const [akun, setAkun] = useState("KAS");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, i, s] = await Promise.all([
        api.get("/material-bills"),
        api.get("/material-bills/invoices", { params: tglFilter ? { tanggal: tglFilter } : {} }),
        api.get("/material-bills/saldo"),
      ]);
      setBills(b.data);
      setInvoices(i.data);
      setSaldo(s.data);
    } catch {
      toast.error("Gagal memuat data tagihan bahan");
    }
  }, [tglFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const buatInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/material-bills/invoices", { kategori: sel });
      toast.success(`Invoice ${data.invoice_no} dibuat · ${rp(data.total)}`);
      setSel([]);
      setActive({ ...data, terbayar: 0, sisa: data.total, status: "BELUM BAYAR" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat invoice bahan");
    } finally {
      setBusy(false);
    }
  };

  const bayar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/material-bills/invoices/${active.invoice_no}/pay`, {
        jumlah: Number(jumlah) || 0,
        akun,
      });
      toast.success(
        `Bayar ${rp(data.jumlah)} · ${data.nomor} · ${data.status} · sisa ${rp(data.sisa)}`
      );
      setJumlah("");
      const { data: list } = await api.get("/material-bills/invoices");
      setActive(list.find((x) => x.invoice_no === active.invoice_no) || null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membayar invoice bahan");
    } finally {
      setBusy(false);
    }
  };

  const totalBelum = bills.reduce((a, b) => a + Number(b.belum_bayar || 0), 0);

  return (
    <div className="space-y-5" data-testid="material-bill-board">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card label="Tagihan Belum Diinvoice" value={rp(bills.reduce((a, b) => a + b.total, 0))} warna="text-slate-100" />
        <Card label="Belum Dibayar" value={rp(totalBelum)} warna="text-rose-300" />
        <Card
          label="Saldo Tersedia"
          value={rp(saldo.saldo)}
          warna="text-emerald-300"
          extra={`Nomor pengeluaran berikutnya: ${saldo.nomor_berikutnya}`}
        />
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="material-bills">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-200">
              Tagihan Bahan per Kategori
            </h4>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Pilih kategori untuk dibuatkan invoice · kategori yang sudah diinvoice tidak tampil lagi
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="bill-make-invoice-btn"
              onClick={buatInvoice}
              disabled={busy || !sel.length}
              className="h-10 rounded-xl bg-blue-600 hover:bg-blue-500 px-4 text-xs font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" /> Buat Invoice ({sel.length})</>}
            </Button>
            <Button
              data-testid="bill-refresh-btn"
              onClick={load}
              variant="outline"
              className="h-10 rounded-xl border-white/10 bg-slate-800/60"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["", "Kategori", "Qty Keluar", "Total Tagihan", "User Entri", "Mutasi Terakhir"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="material-bill-list">
              {bills.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                    Tidak ada tagihan bahan baru. Semua stok keluar sudah diinvoice.
                  </td>
                </tr>
              )}
              {bills.map((b) => (
                <tr key={b.kategori} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`bill-pick-${b.kategori}`}
                      checked={sel.includes(b.kategori)}
                      onChange={() =>
                        setSel((p) =>
                          p.includes(b.kategori) ? p.filter((x) => x !== b.kategori) : [...p, b.kategori]
                        )
                      }
                      className="w-4 h-4 accent-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-slate-200 font-medium">{b.kategori}</td>
                  <td className="px-3 py-2 text-slate-400">{b.qty}</td>
                  <td className="px-3 py-2 text-rose-300 font-semibold">{rp(b.total)}</td>
                  <td className="px-3 py-2 text-slate-500">{b.user_terakhir || "-"}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {b.tanggal_terakhir ? fmtDate(b.tanggal_terakhir) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="bill-invoice-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Daftar Invoice Bahan Tercetak
          </h4>
          <Field label="Filter Tanggal">
            <Input
              data-testid="bill-invoice-date-filter"
              type="date"
              value={tglFilter}
              onChange={(e) => setTglFilter(e.target.value)}
              className="h-10 bg-slate-900/70 border-white/10"
            />
          </Field>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["No Invoice", "Tanggal", "Kategori", "Total", "Terbayar", "Sisa", "Status", "User Entri", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="bill-invoice-list">
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    Belum ada invoice bahan pada filter ini.
                  </td>
                </tr>
              )}
              {invoices.map((i) => (
                <tr key={i.invoice_no} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 font-semibold text-blue-300">{i.invoice_no}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(i.tanggal)}</td>
                  <td className="px-3 py-2">{(i.kategori || []).join(", ")}</td>
                  <td className="px-3 py-2">{rp(i.total)}</td>
                  <td className="px-3 py-2 text-emerald-300">{rp(i.terbayar)}</td>
                  <td className="px-3 py-2 text-rose-300">{rp(i.sisa)}</td>
                  <td className="px-3 py-2 text-[10px] uppercase text-slate-400">{i.status}</td>
                  <td className="px-3 py-2 text-slate-500">{(i.entri_oleh || []).join(", ") || "-"}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`bill-invoice-open-${i.invoice_no}`}
                      onClick={() => {
                        setActive(i);
                        setJumlah("");
                      }}
                      className="rounded-lg border border-white/10 bg-slate-800/70 px-3 py-1 text-[10px] font-semibold uppercase hover:bg-slate-700"
                    >
                      Buka
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <>
          <div className="glass rounded-2xl p-5 md:p-6 flex flex-wrap items-end gap-4">
            <Field label="Nominal Bayar">
              <Input
                data-testid="bill-amount-input"
                placeholder={`maks ${rp(Math.min(active.sisa ?? active.total, saldo.saldo))}`}
                value={jumlah}
                onChange={(e) => setJumlah(e.target.value)}
                className="h-11 w-40 bg-slate-900/70 border-white/10"
              />
            </Field>
            <Field label="Via">
              <select
                data-testid="bill-akun-select"
                value={akun}
                onChange={(e) => setAkun(e.target.value)}
                className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
              >
                {["KAS", "BANK"].map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Button
              data-testid="bill-pay-btn"
              onClick={bayar}
              disabled={busy || !jumlah || (active.sisa ?? active.total) <= 0}
              className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-5"
            >
              <Wallet className="w-4 h-4 mr-2" /> Bayar
            </Button>
            <Button
              data-testid="bill-print-a4-btn"
              onClick={() => printArea("bill-invoice-print", false)}
              variant="outline"
              className="h-11 rounded-xl border-white/10 bg-slate-800/60 px-5"
            >
              <Printer className="w-4 h-4 mr-2" /> Print Invoice A4
            </Button>
            <Button
              data-testid="bill-print-struk-btn"
              onClick={() => printArea("bill-struk-print", true)}
              variant="outline"
              className="h-11 rounded-xl border-white/10 bg-slate-800/60 px-5"
            >
              <Printer className="w-4 h-4 mr-2" /> Print Struk 58mm
            </Button>
          </div>

          <div className="glass rounded-2xl p-6 grid gap-6 lg:grid-cols-[1fr_260px]">
            <div id="bill-invoice-print" data-testid="bill-invoice-preview" className="bg-white text-black p-5">
              <div style={{ borderBottom: "3px solid #2a8c9c", paddingBottom: 6, display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#0f5f6d" }}>{COMPANY.name}</div>
                  <div style={{ fontSize: 9 }}>{COMPANY.address}</div>
                  <div style={{ fontSize: 9 }}>{COMPANY.phone} · {COMPANY.email}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f5f6d" }}>INVOICE BAHAN</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{active.invoice_no}</div>
                  <div style={{ fontSize: 10 }}>{fmtDate(active.tanggal)}</div>
                </div>
              </div>
              <div style={{ fontSize: 10, marginTop: 6 }}>
                Kategori: <b>{(active.kategori || []).join(", ")}</b> · User entri:{" "}
                <b>{(active.entri_oleh || []).join(", ") || "-"}</b>
              </div>
              <table style={{ marginTop: 8 }}>
                <thead>
                  <tr style={{ background: "#a5dde5" }}>
                    {["Tanggal", "Kategori", "Jenis Bahan", "Qty", "Harga Jual", "Jumlah", "User"].map((h) => (
                      <th key={h} style={{ border: "1px solid #2a8c9c", padding: "3px 5px", fontSize: 10 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(active.lines || []).map((l) => (
                    <tr key={l.id}>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px" }}>{l.tanggal}</td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px" }}>{l.kategori}</td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px" }}>
                        {l.jenis_bahan} {l.merk ? `· ${l.merk}` : ""}
                      </td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px", textAlign: "center" }}>{l.qty}</td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px", textAlign: "right" }}>{rp(l.harga_jual)}</td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px", textAlign: "right" }}>{rp(l.nilai_jual)}</td>
                      <td style={{ border: "1px solid #2a8c9c", padding: "3px 5px" }}>{l.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: 11, textAlign: "right" }}>
                <div>JUMLAH : <b>{rp(active.total)}</b></div>
                <div>TERBAYAR : {rp(active.terbayar)}</div>
                <div>SISA : <b>{rp(active.sisa ?? active.total)}</b></div>
                <div style={{ fontSize: 9, marginTop: 4 }}>Status: {active.status}</div>
              </div>
            </div>

            <div
              id="bill-struk-print"
              data-testid="bill-struk-preview"
              className="bg-white text-black w-[240px] p-3 text-[11px] font-mono mx-auto"
            >
              <div style={{ textAlign: "center", fontWeight: 700 }}>{COMPANY.name}</div>
              <div style={{ textAlign: "center", fontSize: 8 }}>{COMPANY.phone}</div>
              <div style={{ borderTop: "1px dashed #000", marginTop: 4, paddingTop: 4 }}>
                <div>No : {active.invoice_no}</div>
                <div>Tgl : {fmtDate(active.tanggal)}</div>
                <div>Kat : {(active.kategori || []).join(", ")}</div>
                <div>Entri : {(active.entri_oleh || []).join(", ") || "-"}</div>
              </div>
              <div style={{ borderTop: "1px dashed #000", marginTop: 4, paddingTop: 4 }}>
                {(active.lines || []).map((l) => (
                  <div key={`s-${l.id}`} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{l.jenis_bahan} x{l.qty}</span>
                    <span>{rp(l.nilai_jual)}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: "1px dashed #000", marginTop: 4, paddingTop: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>TOTAL</span><b>{rp(active.total)}</b></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>BAYAR</span><span>{rp(active.terbayar)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>SISA</span><span>{rp(active.sisa ?? active.total)}</span></div>
              </div>
              <div style={{ textAlign: "center", fontSize: 8, borderTop: "1px dashed #000", marginTop: 4, paddingTop: 3 }}>
                {active.status} · terima kasih
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
